import { collection, getDocs, getDoc, query, where, orderBy, limit, Timestamp, deleteDoc, doc, documentId } from 'firebase/firestore';
import { db } from '../storage/firebase';

export interface SessionData {
  id: string;
  squadId: string;
  squadTitle: string;
  curriculumId?: string;
  teacherId: string;
  teacherName: string;
  startTime: Timestamp;
  endTime?: Timestamp;
  duration: number; // in milliseconds
  attendanceData: Record<string, boolean>;
  attendanceCount: number;
  totalStudents: number;
  attendanceRate: number; // percentage
  levelId: string;
  levelTitle: string;
  location: {
    latitude: number;
    longitude: number;
    accuracy: number;
    timestamp: Timestamp;
  };
  expectedLocation?: {
    latitude: number;
    longitude: number;
    address: string;
  };
  distanceFromExpected?: number; // in meters
  locationStatus: 'on-site' | 'near-site' | 'off-site' | 'unknown';
  completionPercentage: number;
  scheduledStartTime?: string; // expected class start time
  scheduledDuration?: number; // expected duration in minutes
  feedbackFormId?: string;
  teacherRating?: number;
  studentRating5?: number;
  altSessionId?: string; // if liveSessions doc has a separate sessionId in payload
  isLive?: boolean; // indicates if this is a currently active session
}

export interface FilterOptions {
  teacherId?: string;
  squadId?: string;
  dateFrom?: Date;
  dateTo?: Date;
  locationStatus?: string;
  minAttendance?: number;
  studentId?: string;
  levelNumber?: number;
}

export interface TeacherOption {
  id: string;
  name: string;
  email: string;
}

export interface SquadOption {
  id: string;
  title: string;
  teacherName: string;
  schoolName: string;
}

export interface StudentOption {
  id: string;
  name: string;
}

export interface StudentProfile {
  id: string;
  displayName: string;
  photoURL?: string;
}

class SessionsService {
  /**
   * Calculate the distance between two geographic points using the Haversine formula
   */
  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  /**
   * Determine location status based on distance from expected location
   */
  private getLocationStatus(distance: number): 'on-site' | 'near-site' | 'off-site' {
    if (distance <= 100) return 'on-site'; // Within 100m
    if (distance <= 500) return 'near-site'; // Within 500m
    return 'off-site';
  }

  /**
   * Calculate completion percentage based on mission progress and level completion
   */
  private calculateCompletionPercentage(
    missionProgress: Record<string, Record<string, boolean | number>>,
    levelId: string,
    completedLevels: string[]
  ): number {
    // If level is completed, it's 100%
    if (completedLevels.includes(levelId)) {
      return 100;
    }

    // Calculate based on mission progress for the current level
    const levelProgress = missionProgress[levelId];
    if (!levelProgress) {
      return 0;
    }

    const missions = Object.values(levelProgress);
    const completedMissions = missions.filter(mission => mission === true).length;
    
    return Math.round((completedMissions / missions.length) * 100) || 0;
  }

  /**
   * Fetch live sessions with associated squad and teacher data
   */
  async fetchSessions(filters: FilterOptions = {}): Promise<SessionData[]> {
    try {
      // To avoid complex composite index requirements, we'll fetch more broadly and filter in memory
      let sessionsQuery;
      const queryConstraints = [];
      
      // Start with time-based filtering (most selective)
      if (filters.dateFrom) {
        queryConstraints.push(where('startTime', '>=', Timestamp.fromDate(filters.dateFrom)));
      }
      
      if (filters.dateTo) {
        queryConstraints.push(where('startTime', '<=', Timestamp.fromDate(filters.dateTo)));
      }

      // Avoid adding equality constraints on teacherId/squadId to prevent composite index requirements.
      // We'll filter these in-memory below.

      // Add ordering and limit
      queryConstraints.push(orderBy('startTime', 'desc'));
      queryConstraints.push(limit(200)); // Increased limit since we'll filter in memory

      // Create the query
      sessionsQuery = query(collection(db, 'liveSessions'), ...queryConstraints);

      const sessionsSnapshot = await getDocs(sessionsQuery);
      
      // Fetch all squads for location and schedule data, and to check for live sessions
      const squadsSnapshot = await getDocs(collection(db, 'squads'));
      const squadsData = new Map();
      const liveSquads = new Map(); // Store squads with active live sessions
      
      squadsSnapshot.docs.forEach(doc => {
        const squadData = { id: doc.id, ...doc.data() } as any;
        squadsData.set(doc.id, squadData);
        
        // Check if this squad has an active live session
        if (squadData.liveSession && squadData.liveSession.sessionId) {
          liveSquads.set(squadData.liveSession.sessionId, squadData);
        }
      });

      // Process sessions and enrich with squad data
      const sessionsData: SessionData[] = [];
      const docIdToAltSessionId = new Map<string, string>();
      const processedLiveSessionIds = new Set(); // Track which live sessions we've processed

      // Process completed sessions from liveSessions collection
      for (const sessionDoc of sessionsSnapshot.docs) {
        const sessionData = sessionDoc.data() as any;
        
        // Skip sessions that have been explicitly marked as canceled
        if (sessionData.status === 'canceled') {
          continue;
        }

        const squadData = squadsData.get(sessionData.squadId) as any;

        if (!squadData) continue; // Skip if squad data not found

        // Check if this is also a live session
        const isLive = liveSquads.has(sessionDoc.id);
        if (isLive) {
          processedLiveSessionIds.add(sessionDoc.id);
        }

        const attendanceEntries = Object.entries(sessionData.attendanceData || {});
        const attendedCount = attendanceEntries.filter(([_, attended]) => attended).length;
        const totalStudents = attendanceEntries.length;
        const attendanceRate = totalStudents > 0 ? (attendedCount / totalStudents) * 100 : 0;

        // Calculate distance from expected location
        let distanceFromExpected = 0;
        let locationStatus: 'on-site' | 'near-site' | 'off-site' | 'unknown' = 'unknown';
        
        if (sessionData.location && squadData.location) {
          distanceFromExpected = this.calculateDistance(
            sessionData.location.latitude,
            sessionData.location.longitude,
            squadData.location.latitude,
            squadData.location.longitude
          );
          locationStatus = this.getLocationStatus(distanceFromExpected);
        }

        // Calculate completion percentage
        const completionPercentage = this.calculateCompletionPercentage(
          squadData.missionProgress || {},
          sessionData.levelId,
          squadData.completedLevels || []
        );

        // Apply in-memory filters for teacher/squad
        if (filters.teacherId && sessionData.teacherId !== filters.teacherId) continue;
        if (filters.squadId && sessionData.squadId !== filters.squadId) continue;

        // Apply student filter (present-only)
        if (filters.studentId && !(sessionData.attendanceData || {})[filters.studentId]) continue;

        // Apply additional filters
        if (filters.minAttendance && attendanceRate < filters.minAttendance) {
          continue;
        }

        if (filters.locationStatus && locationStatus !== filters.locationStatus) {
          continue;
        }

        // Apply level number filter
        if (filters.levelNumber != null) {
          const parseLevelNumber = (id: any, title: any): number | undefined => {
            // try id first: may be numeric string like '3'
            const idNum = typeof id === 'string' ? parseInt(id, 10) : (typeof id === 'number' ? id : undefined);
            if (idNum != null && !Number.isNaN(idNum)) return idNum;
            // fallback to extracting from title like 'Level 3'
            if (typeof title === 'string') {
              const m = title.match(/(\d+)/);
              if (m) {
                const n = parseInt(m[1], 10);
                if (!Number.isNaN(n)) return n;
              }
            }
            return undefined;
          };
          const levelNum = parseLevelNumber(sessionData.levelId, sessionData.levelTitle);
          if (levelNum !== filters.levelNumber) continue;
        }

        const session: SessionData = {
          id: sessionDoc.id,
          squadId: sessionData.squadId,
          squadTitle: sessionData.squadTitle || squadData.title,
          curriculumId: squadData.curriculumId || 'music',
          teacherId: sessionData.teacherId,
          teacherName: sessionData.teacherName,
          startTime: sessionData.startTime,
          endTime: sessionData.endTime,
          duration: sessionData.duration || 0,
          attendanceData: sessionData.attendanceData || {},
          attendanceCount: attendedCount,
          totalStudents,
          attendanceRate: Math.round(attendanceRate),
          levelId: sessionData.levelId,
          levelTitle: sessionData.levelTitle,
          location: sessionData.location,
          expectedLocation: squadData.location ? {
            latitude: squadData.location.latitude,
            longitude: squadData.location.longitude,
            address: squadData.location.address || squadData.address
          } : undefined,
          distanceFromExpected,
          locationStatus,
          completionPercentage,
          scheduledStartTime: squadData.schedule?.startTime,
          scheduledDuration: squadData.schedule ? this.parseScheduledDuration(squadData.schedule.startTime, squadData.schedule.endTime) : undefined,
          feedbackFormId: sessionData.feedbackFormId, // Assuming this exists
          teacherRating: sessionData.teacherRating, // Assuming this exists
          altSessionId: sessionData.sessionId ? String(sessionData.sessionId) : undefined,
          isLive,
        };

        // Track alternate sessionId if stored in document (may differ from doc id)
        if (sessionData.sessionId) {
          docIdToAltSessionId.set(sessionDoc.id, String(sessionData.sessionId));
        }

        sessionsData.push(session);
      }

      // After collecting completed sessions, try to attach ratings from feedback forms
      try {
        const candidateIds = Array.from(new Set([
          ...sessionsData.map(s => s.id),
          ...Array.from(docIdToAltSessionId.values())
        ]));
        const feedbackMap = await this.fetchFeedbackBySessionIds(candidateIds);
        for (const s of sessionsData) {
          const altId = docIdToAltSessionId.get(s.id);
          const fb = feedbackMap.get(s.id) || (altId ? feedbackMap.get(altId) : undefined);
          if (fb) {
            if (fb.rating10 != null) s.teacherRating = fb.rating10;
            if (fb.id) s.feedbackFormId = fb.id;
          }
        }
      } catch (e) {
        console.warn('Failed to attach ratings to sessions:', e);
      }

      // Attach student emoji rating averages (x/5) aggregated from studentFeedback
      try {
        const candidateIdsForStudentFb = Array.from(new Set([
          ...sessionsData.map(s => s.id),
          ...Array.from(docIdToAltSessionId.values())
        ]));
        const studentAverages = await this.fetchStudentEmojiAveragesBySessionIds(candidateIdsForStudentFb);
        for (const s of sessionsData) {
          const altId = docIdToAltSessionId.get(s.id);
          const avg = studentAverages.get(s.id) || (altId ? studentAverages.get(altId) : undefined);
          if (avg != null) s.studentRating5 = avg;
        }
      } catch (e) {
        console.warn('Failed to attach student emoji ratings:', e);
      }

      // Process live-only sessions (sessions that are active but not yet in liveSessions collection)
      for (const [sessionId, squadData] of liveSquads.entries()) {
        // Skip if we already processed this as a completed session
        if (processedLiveSessionIds.has(sessionId)) continue;

        const liveSession = squadData.liveSession;
        if (!liveSession) continue;

        // Apply filters for live sessions
        if (filters.teacherId && liveSession.teacherId !== filters.teacherId) continue;
        if (filters.squadId && squadData.id !== filters.squadId) continue;

        // Calculate attendance from squad attendance data
        const attendanceEntries = Object.entries(squadData.attendance || {});
        const attendedCount = attendanceEntries.filter(([_, attended]) => attended).length;
        const totalStudents = (squadData.studentIds || []).length;
        const attendanceRate = totalStudents > 0 ? (attendedCount / totalStudents) * 100 : 0;

        // Apply student filter (present-only) for live sessions
        if (filters.studentId) {
          const attended = (squadData.attendance || {})[filters.studentId];
          if (!attended) continue;
        }

        // Apply attendance filter
        if (filters.minAttendance && attendanceRate < filters.minAttendance) continue;

        // Calculate distance from expected location
        let distanceFromExpected = 0;
        let locationStatus: 'on-site' | 'near-site' | 'off-site' | 'unknown' = 'unknown';
        
        if (liveSession.location && squadData.location) {
          distanceFromExpected = this.calculateDistance(
            liveSession.location.latitude,
            liveSession.location.longitude,
            squadData.location.latitude,
            squadData.location.longitude
          );
          locationStatus = this.getLocationStatus(distanceFromExpected);
        }

        // Apply location filter
        if (filters.locationStatus && locationStatus !== filters.locationStatus) continue;

        // Apply level number filter for live sessions
        if (filters.levelNumber != null) {
          const id = squadData.currentLessonId || liveSession.levelId || '1';
          const num = typeof id === 'string' ? parseInt(id, 10) : (typeof id === 'number' ? id : undefined);
          if (num == null || Number.isNaN(num) || num !== filters.levelNumber) continue;
        }

        // Calculate completion percentage (for live sessions, use current progress)
        const completionPercentage = this.calculateCompletionPercentage(
          squadData.missionProgress || {},
          squadData.currentLessonId || '1',
          squadData.completedLevels || []
        );

        // Calculate duration (time since start)
        const currentTime = new Date().getTime();
        const startTime = liveSession.startTime.toMillis();
        const duration = currentTime - startTime;

        const liveSessionData: SessionData = {
          id: sessionId,
          squadId: squadData.id,
          squadTitle: squadData.title,
          curriculumId: squadData.curriculumId || 'music',
          teacherId: liveSession.teacherId,
          teacherName: liveSession.teacherName,
          startTime: liveSession.startTime,
          endTime: undefined, // Live session hasn't ended yet
          duration,
          attendanceData: squadData.attendance || {},
          attendanceCount: attendedCount,
          totalStudents,
          attendanceRate: Math.round(attendanceRate),
          levelId: squadData.currentLessonId || '1',
          levelTitle: `Level ${squadData.currentLessonId || '1'}`,
          location: liveSession.location ? {
            latitude: liveSession.location.latitude,
            longitude: liveSession.location.longitude,
            accuracy: liveSession.location.accuracy,
            timestamp: liveSession.location.timestamp instanceof Date 
              ? Timestamp.fromDate(liveSession.location.timestamp)
              : liveSession.location.timestamp || Timestamp.now(),
          } : {
            latitude: 0,
            longitude: 0,
            accuracy: 0,
            timestamp: Timestamp.now(),
          },
          expectedLocation: squadData.location ? {
            latitude: squadData.location.latitude,
            longitude: squadData.location.longitude,
            address: squadData.location.address || squadData.address || 'Unknown'
          } : undefined,
          distanceFromExpected,
          locationStatus,
          completionPercentage,
          scheduledStartTime: squadData.schedule?.startTime,
          scheduledDuration: squadData.schedule ? this.parseScheduledDuration(squadData.schedule.startTime, squadData.schedule.endTime) : undefined,
          feedbackFormId: undefined, // Live sessions don't have feedback yet
          teacherRating: undefined, // Live sessions don't have ratings yet
          isLive: true,
        };

        sessionsData.push(liveSessionData);
      }

      return sessionsData;
    } catch (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }
  }

  /**
   * Parse scheduled duration from start and end times
   */
  private parseScheduledDuration(startTime: string, endTime: string): number {
    if (!startTime || !endTime) return 0;
    
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;
    
    return endMinutes - startMinutes;
  }

  /**
   * Fetch teacher options for filter dropdown
   */
  async fetchTeachers(): Promise<TeacherOption[]> {
    try {
      const teachersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'teacher')
      );
      
      const snapshot = await getDocs(teachersQuery);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().displayName || doc.data().email || 'Unknown',
        email: doc.data().email || ''
      }));
    } catch (error) {
      console.error('Error fetching teachers:', error);
      return [];
    }
  }

  /**
   * Fetch squad options for filter dropdown
   */
  async fetchSquads(): Promise<SquadOption[]> {
    try {
      const squadsSnapshot = await getDocs(collection(db, 'squads'));
      
      // Also fetch teachers to get names
      const teachersSnapshot = await getDocs(
        query(collection(db, 'users'), where('role', '==', 'teacher'))
      );
      const teachersMap = new Map();
      teachersSnapshot.docs.forEach(doc => {
        teachersMap.set(doc.id, doc.data().displayName || doc.data().email || 'Unknown');
      });

      return squadsSnapshot.docs.map(doc => {
        const data = doc.data();
        const primaryTeacherId = data.regularTeacher || data.teacherIds?.[0];
        
        return {
          id: doc.id,
          title: data.title || 'Unknown Squad',
          teacherName: primaryTeacherId ? teachersMap.get(primaryTeacherId) || 'Unknown' : 'No Teacher',
          schoolName: data.locationName || data.location?.name || 'Unknown School'
        };
      });
    } catch (error) {
      console.error('Error fetching squads:', error);
      return [];
    }
  }

  /**
   * Fetch student options for filter dropdown
   */
  async fetchStudents(): Promise<StudentOption[]> {
    try {
      const studentsSnapshot = await getDocs(
        query(collection(db, 'users'), where('role', '==', 'student'))
      );
      return studentsSnapshot.docs.map(doc => ({
        id: doc.id,
        name: (doc.data() as any).displayName || (doc.data() as any).email || 'Unknown'
      }));
    } catch (error) {
      console.error('Error fetching students:', error);
      return [];
    }
  }

  /**
   * Resolve a list of student IDs to display names (best-effort)
   */
  async fetchStudentNamesByIds(studentIds: string[]): Promise<Record<string, string>> {
    const idToName: Record<string, string> = {};
    try {
      const uniqueIds = Array.from(new Set(studentIds)).filter(Boolean);
      for (const studentId of uniqueIds) {
        const snap = await getDoc(doc(db, 'users', studentId));
        if (snap.exists()) {
          const data = snap.data() as any;
          idToName[studentId] = data.displayName || data.email || 'Unknown';
        } else {
          idToName[studentId] = 'Unknown';
        }
      }
    } catch (error) {
      console.error('Error resolving student names:', error);
    }
    return idToName;
  }

  /**
   * Load lightweight student profiles (name + photoURL) for given IDs
   */
  async fetchStudentProfilesByIds(studentIds: string[]): Promise<Record<string, StudentProfile>> {
    const profiles: Record<string, StudentProfile> = {};
    try {
      const uniqueIds = Array.from(new Set(studentIds)).filter(Boolean);
      for (const studentId of uniqueIds) {
        const snap = await getDoc(doc(db, 'users', studentId));
        if (snap.exists()) {
          const data = snap.data() as any;
          profiles[studentId] = {
            id: studentId,
            displayName: data.displayName || data.email || 'Unknown',
            photoURL: data.photoURL || data.avatarUrl || undefined,
          };
        }
      }
    } catch (error) {
      console.error('Error fetching student profiles:', error);
    }
    return profiles;
  }

  /**
   * Fetch feedback form data for a specific session
   */
  async fetchFeedbackForm(sessionId: string): Promise<any> {
    try {
      console.log('🔍 Fetching feedback for sessionId:', sessionId);
      
      // First, get the session document to find the internal sessionId field
      let internalSessionId: string | null = null;
      try {
        const sessionDoc = await getDoc(doc(db, 'liveSessions', sessionId));
        if (sessionDoc.exists()) {
          const sessionData = sessionDoc.data();
          internalSessionId = sessionData?.sessionId || null;
          console.log('📋 Found internal sessionId in session doc:', internalSessionId);
        }
      } catch (e) {
        console.warn('Could not fetch session document:', e);
      }
      
      // Try direct document lookup first using the document ID
      const directDoc = await getDoc(doc(db, 'classFeedback', sessionId));
      console.log('📄 Direct doc lookup (doc ID) result:', directDoc.exists() ? 'FOUND' : 'NOT FOUND');
      
      if (directDoc.exists()) {
        const data = directDoc.data();
        console.log('✅ Feedback data found via direct lookup:', data);
        return this.mapFeedbackData(directDoc.id, sessionId, data);
      }
      
      // Try using the internal sessionId if it exists and differs from the document ID
      if (internalSessionId && internalSessionId !== sessionId) {
        console.log('🔎 Trying internal sessionId:', internalSessionId);
        const internalDoc = await getDoc(doc(db, 'classFeedback', internalSessionId));
        console.log('📄 Internal sessionId lookup result:', internalDoc.exists() ? 'FOUND' : 'NOT FOUND');
        
        if (internalDoc.exists()) {
          const data = internalDoc.data();
          console.log('✅ Feedback data found via internal sessionId:', data);
          return this.mapFeedbackData(internalDoc.id, sessionId, data);
        }
      }
      
      // Fallback to query by sessionId field
      console.log('🔎 Trying fallback query by sessionId field...');
      const feedbackQuery = query(
        collection(db, 'classFeedback'),
        where('sessionId', '==', sessionId)
      );
      
      const snapshot = await getDocs(feedbackQuery);
      console.log('📊 Query result:', snapshot.empty ? 'EMPTY' : `FOUND ${snapshot.size} docs`);
      
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        const data = doc.data();
        return this.mapFeedbackData(doc.id, sessionId, data);
      }
      
      // Final fallback: try querying with internal sessionId if we have it
      if (internalSessionId && internalSessionId !== sessionId) {
        console.log('🔎 Final fallback: query by internal sessionId field...');
        const internalQuery = query(
          collection(db, 'classFeedback'),
          where('sessionId', '==', internalSessionId)
        );
        const internalSnapshot = await getDocs(internalQuery);
        console.log('📊 Internal query result:', internalSnapshot.empty ? 'EMPTY' : `FOUND ${internalSnapshot.size} docs`);
        
        if (!internalSnapshot.empty) {
          const doc = internalSnapshot.docs[0];
          const data = doc.data();
          return this.mapFeedbackData(doc.id, sessionId, data);
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching feedback form:', error);
      return null;
    }
  }

  /**
   * Helper method to map feedback data to the expected format
   */
  private mapFeedbackData(docId: string, sessionId: string, data: any): any {
    return {
      id: docId,
      sessionId: sessionId,
      teacherId: data?.teacherId || '',
      teacherName: data?.teacherName || '',
      squadTitle: data?.squadName || data?.squadTitle || '',
      submittedAt: data?.createdAt || data?.submittedAt,
      
      // Map common rating fields
      overallRating: data?.sessionRating || data?.overallRating || data?.rating,
      studentEngagement: data?.engagement || data?.studentEngagement,
      lessonEffectiveness: data?.effectiveness || data?.lessonEffectiveness,
      classroomManagement: data?.management || data?.classroomManagement,
      timeManagement: data?.timing || data?.timeManagement,
      
      // Map text fields
      highlights: data?.highlights || data?.positives || data?.whatWorked,
      challenges: data?.challenges || data?.difficulties || data?.issues,
      improvements: data?.improvements || data?.suggestions || data?.betterNextTime,
      technicalIssues: data?.technicalIssues || data?.technical || data?.bugs,
      studentBehavior: data?.behavior || data?.studentBehavior,
      additionalNotes: data?.notes || data?.additionalNotes || data?.comments,
      
      // Other fields
      wouldRecommend: data?.recommend !== undefined ? data.recommend : data?.wouldRecommend,
      duration: data?.duration,
      location: data?.location,
      attendanceNotes: data?.attendance || data?.attendanceNotes,
      
      // Include all original data for debugging
      ...data
    };
  }

  /**
   * Batch-load feedback forms for a set of sessionIds (chunks of 10 for Firestore 'in' limit)
   */
  private async fetchFeedbackBySessionIds(sessionIds: string[]): Promise<Map<string, { id: string; rating10?: number }>> {
    const map = new Map<string, { id: string; rating10?: number }>();
    try {
      const unique = Array.from(new Set(sessionIds)).filter(Boolean);
      const chunkSize = 10;
      for (let i = 0; i < unique.length; i += chunkSize) {
        const chunk = unique.slice(i, i + chunkSize);
        const qRef = query(
          collection(db, 'classFeedback'),
          where('sessionId', 'in', chunk)
        );
        const snap = await getDocs(qRef);
        snap.forEach(d => {
          const data = d.data() as any;
          const raw = data.overallRating ?? data.teacherRating ?? data.sessionRating;
          const num = typeof raw === 'string' ? Number(raw) : (typeof raw === 'number' ? raw : undefined);
          const rating10 = num != null && !Number.isNaN(num) ? Math.max(0, Math.min(10, num)) : undefined;
          if (data.sessionId) {
            map.set(String(data.sessionId), { id: d.id, rating10 });
          }
        });
      }
    } catch (err) {
      console.error('Error batch loading feedback forms:', err);
    }
    return map;
  }

  /**
   * Batch-load student emoji ratings (Block A question 2: enjoyment-<level>) and compute average per session
   */
  private async fetchStudentEmojiAveragesBySessionIds(sessionIds: string[]): Promise<Map<string, number>> {
    const averages = new Map<string, number>();
    try {
      const unique = Array.from(new Set(sessionIds)).filter(Boolean);
      const chunkSize = 10; // Firestore 'in' limit
      const sums = new Map<string, { sum: number; count: number }>();

      for (let i = 0; i < unique.length; i += chunkSize) {
        const chunk = unique.slice(i, i + chunkSize);
        const qRef = query(collection(db, 'studentFeedback'), where('sessionId', 'in', chunk));
        const snap = await getDocs(qRef);
        snap.forEach(d => {
          const data = d.data() as any;
          const sId = String(data.sessionId || '');
          if (!sId) return;
          // Use any enjoyment-* key present in blockA (to avoid coupling to levelId field differences)
          let emojiVal: any;
          const blockA = data?.blockA || {};
          // Support curriculum-prefixed keys like 'ai-enjoyment-1' as well as 'enjoyment-1'
          const enjoymentEntryKey = Object.keys(blockA).find(k => /(^|-)enjoyment-/.test(k));
          if (enjoymentEntryKey) {
            emojiVal = blockA[enjoymentEntryKey]?.emojiValue;
          }
          const val = typeof emojiVal === 'number' ? emojiVal : (typeof emojiVal === 'string' ? Number(emojiVal) : undefined);
          if (val != null && !Number.isNaN(val)) {
            const clamp = Math.max(1, Math.min(5, val));
            const obj = sums.get(sId) || { sum: 0, count: 0 };
            obj.sum += clamp;
            obj.count += 1;
            sums.set(sId, obj);
          }
        });
      }

      for (const [sId, { sum, count }] of sums.entries()) {
        if (count > 0) {
          const avg = Math.round((sum / count) * 10) / 10; // one decimal
          averages.set(sId, avg);
        }
      }
    } catch (err) {
      console.error('Error batch loading student emoji ratings:', err);
    }
    return averages;
  }

  /**
   * Delete a session from the database
   */
  async deleteSession(sessionId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'liveSessions', sessionId));
    } catch (error) {
      console.error('Error deleting session:', error);
      throw error;
    }
  }

  /**
   * Fetch individual student emoji ratings for a session (Block A enjoyment question)
   */
  async fetchStudentEmojiRatings(sessionId: string): Promise<Array<{ studentId: string; value: number; submittedAt?: Timestamp }>> {
    const results: Array<{ studentId: string; value: number; submittedAt?: Timestamp }> = [];
    try {
      const qRef = query(collection(db, 'studentFeedback'), where('sessionId', '==', sessionId));
      const snap = await getDocs(qRef);
      snap.forEach(d => {
        const data = d.data() as any;
        const studentId = String(data.studentId || '');
        if (!studentId) return;
        const blockA = data?.blockA || {};
        // Support curriculum-prefixed keys like 'ai-enjoyment-1' as well as 'enjoyment-1'
        const enjoymentEntryKey = Object.keys(blockA).find(k => /(^|-)enjoyment-/.test(k));
        if (!enjoymentEntryKey) return;
        const raw = blockA[enjoymentEntryKey]?.emojiValue;
        const val = typeof raw === 'number' ? raw : (typeof raw === 'string' ? Number(raw) : undefined);
        if (val == null || Number.isNaN(val)) return;
        const clamped = Math.max(1, Math.min(5, val));
        results.push({ studentId, value: clamped, submittedAt: data.submittedAt });
      });
    } catch (error) {
      console.error('Error fetching student emoji ratings:', error);
    }
    return results;
  }
}

export const sessionsService = new SessionsService();